import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Box,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import LockIcon from '@mui/icons-material/Lock';
import { format, parseISO } from 'date-fns';
import axios from 'axios';

// Thêm component Dialog để xác nhận xóa với passcode
const DeleteConfirmDialog = ({ open, video, passcode, expiresAt, onClose, onConfirm, isLoading, error }) => {
  const [inputPasscode, setInputPasscode] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setInputPasscode('');
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm(inputPasscode);
  };

  // Tính thời gian còn lại (nếu có expiresAt)
  const getRemainingTime = () => {
    if (!expiresAt) return null;
    
    try {
      const expiryTime = new Date(expiresAt);
      const now = new Date();
      const diffMs = expiryTime - now;
      
      if (diffMs <= 0) return 'Đã hết hạn';
      
      const diffMins = Math.floor(diffMs / 60000);
      const diffSecs = Math.floor((diffMs % 60000) / 1000);
      
      return `${diffMins}:${diffSecs.toString().padStart(2, '0')}`;
    } catch (e) {
      return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Xác nhận xóa video</DialogTitle>
      <DialogContent>
        {video && (
          <div>
            <DialogContentText gutterBottom>
              Bạn đang xóa video: <strong>{video.filename}</strong>
            </DialogContentText>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              Hành động này không thể hoàn tác. Video sẽ bị xóa vĩnh viễn.
            </Alert>
            
            {passcode ? (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Mã xác nhận của bạn là: <strong>{passcode}</strong>
                  {expiresAt && (
                    <span> (còn hạn: {getRemainingTime()})</span>
                  )}
                </Alert>
                
                <TextField
                  label="Nhập mã xác nhận"
                  variant="outlined"
                  fullWidth
                  value={inputPasscode}
                  onChange={(e) => setInputPasscode(e.target.value)}
                  error={error !== null}
                  helperText={error}
                  autoFocus
                  margin="normal"
                  inputProps={{ 
                    maxLength: 4,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ my: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography>Đang lấy mã xác nhận...</Typography>
              </Box>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Hủy</Button>
        <Button 
          onClick={handleConfirm} 
          color="error" 
          disabled={isLoading || !passcode || inputPasscode.length !== 4}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Đang xử lý...' : 'Xóa video'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

class RecordedVideos extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      videos: [],
      selectedVideo: null,
      openVideoDialog: false,
      isLoading: false,
      error: null,
      snackbar: {
        open: false,
        message: '',
        severity: 'info'
      },
      isDownloading: false,
      downloadingVideoId: null,
      lastRefreshed: null,
      apiRawResponse: null,
      apiResponseStatus: null,
      
      // Phân trang
      page: 1,
      limit: 10,
      totalPages: 1,
      totalItems: 0,
      
      // Bộ lọc
      showFilters: false,
      filterByPatient: false,
      
      // Xác nhận xóa với passcode
      deleteDialog: {
        open: false,
        video: null,
        passcode: null,
        expiresAt: null,
        isLoading: false,
        error: null
      }
    };
  }

  componentDidMount() {
    // Fetch videos when component mounts
    this.fetchRecordedVideos();
  }

  componentDidUpdate(prevProps) {
    // Refetch if PatientID changes and filterByPatient is true
    if (this.state.filterByPatient && prevProps.PatientID !== this.props.PatientID) {
      this.setState({ page: 1 }, this.fetchRecordedVideos);
    }
  }

  showSnackbar = (message, severity = 'info') => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity
      }
    });
  };

  closeSnackbar = () => {
    this.setState({
      snackbar: {
        ...this.state.snackbar,
        open: false
      }
    });
  };

  // Thay đổi phương thức lấy danh sách video để lấy tất cả video hoặc theo bệnh nhân
  fetchRecordedVideos = async () => {
    const { PatientID } = this.props;
    const { page, limit, filterByPatient } = this.state;
    
    this.setState({ isLoading: true, error: null });
    
    try {
      // Chọn API endpoint dựa vào việc có lọc theo bệnh nhân hay không
      let url;
      if (filterByPatient && PatientID) {
        url = `https://sperms.ailab.vn/api/patient-videos/${PatientID}?page=${page}&limit=${limit}`;
        console.log(`Fetching videos for patient ID: ${PatientID}, page ${page}, limit ${limit}`);
      } else {
        url = `https://sperms.ailab.vn/api/videos?page=${page}&limit=${limit}`;
        console.log(`Fetching all videos, page ${page}, limit ${limit}`);
      }
      
      // Gọi API
      const response = await axios.get(url);
      
      // Lưu trữ raw response và status để debug
      this.setState({
        apiRawResponse: response.data,
        apiResponseStatus: response.status
      });
      
      console.log('API Response:', response.data);
      
      // Cập nhật state với videos và thông tin phân trang
      if (response.data && response.data.videos) {
        this.setState({
          videos: response.data.videos,
          totalPages: response.data.pagination?.pages || 1,
          totalItems: response.data.pagination?.total || 0,
          isLoading: false,
          lastRefreshed: new Date()
        }, () => {
          console.log('Updated state videos length:', this.state.videos.length);
        });
        
        // Hiển thị thông báo
        if (response.data.videos.length > 0) {
          this.showSnackbar(`Đã tải ${response.data.videos.length} video (Trang ${page}/${response.data.pagination?.pages || 1})`, 'success');
        } else {
          this.showSnackbar('Không có video nào được tìm thấy', 'info');
        }
      } else {
        this.setState({ 
          videos: [],
          isLoading: false,
          lastRefreshed: new Date()
        });
        this.showSnackbar('Không có dữ liệu video', 'info');
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      
      this.setState({ 
        isLoading: false,
        error: error.response?.data?.detail || error.message || 'Không thể tải danh sách video',
        videos: []
      });
      
      this.showSnackbar('Lỗi khi làm mới danh sách video: ' + (error.message || ''), 'error');
    }
  };

  handlePageChange = (event, newPage) => {
    this.setState({ page: newPage }, this.fetchRecordedVideos);
  };

  handleLimitChange = (event) => {
    this.setState({ 
      limit: event.target.value,
      page: 1 // Reset về trang 1 khi thay đổi limit
    }, this.fetchRecordedVideos);
  };

  toggleFilters = () => {
    this.setState(prevState => ({
      showFilters: !prevState.showFilters
    }));
  };

  handleFilterChange = () => {
    this.setState(prevState => ({
      filterByPatient: !prevState.filterByPatient,
      page: 1 // Reset về trang 1 khi thay đổi filter
    }), this.fetchRecordedVideos);
  };

  handlePlayVideo = (video) => {
    this.setState({
      selectedVideo: video,
      openVideoDialog: true
    });
  };

  // Thêm phương thức để mở dialog xác nhận xóa và lấy passcode
  handleOpenDeleteDialog = async (video) => {
    this.setState({
      deleteDialog: {
        open: true,
        video,
        passcode: null,
        expiresAt: null,
        isLoading: true,
        error: null
      }
    });
    
    try {
      // Gọi API để tạo passcode
      const response = await axios.post(`https://sperms.ailab.vn/api/generate-delete-passcode/${video.id}`);
      
      if (response.data && response.data.passcode) {
        this.setState({
          deleteDialog: {
            ...this.state.deleteDialog,
            passcode: response.data.passcode,
            expiresAt: response.data.expiresAt,
            isLoading: false
          }
        });
      } else {
        throw new Error('Không nhận được mã xác nhận từ server');
      }
    } catch (error) {
      console.error('Error generating passcode:', error);
      
      this.setState({
        deleteDialog: {
          ...this.state.deleteDialog,
          isLoading: false,
          error: 'Không thể tạo mã xác nhận: ' + (error.response?.data?.detail || error.message || 'Lỗi không xác định')
        }
      });
      
      this.showSnackbar('Lỗi khi tạo mã xác nhận', 'error');
    }
  };

  handleCloseDeleteDialog = () => {
    this.setState({
      deleteDialog: {
        open: false,
        video: null,
        passcode: null,
        expiresAt: null,
        isLoading: false,
        error: null
      }
    });
  };

  // Sửa phương thức xóa video để sử dụng passcode
  handleDeleteVideo = async (passcode) => {
    const { video } = this.state.deleteDialog;
    
    if (!video) return;
    
    this.setState({
      deleteDialog: {
        ...this.state.deleteDialog,
        isLoading: true,
        error: null
      }
    });
    
    try {
      // Gọi API xóa video với passcode
      await axios.delete(`https://sperms.ailab.vn/api/delete-video/${video.id}`, {
        data: { passcode }
      });
      
      // Đóng dialog
      this.handleCloseDeleteDialog();
      
      // Đóng dialog xem video nếu đang mở video đó
      if (this.state.selectedVideo && this.state.selectedVideo.id === video.id) {
        this.setState({ openVideoDialog: false, selectedVideo: null });
      }
      
      // Refresh the list after deletion
      this.fetchRecordedVideos();
      this.showSnackbar('Đã xóa video thành công', 'success');
    } catch (error) {
      console.error('Error deleting video:', error);
      
      // Hiển thị lỗi trong dialog
      this.setState({
        deleteDialog: {
          ...this.state.deleteDialog,
          isLoading: false,
          error: error.response?.data?.detail || error.message || 'Không thể xóa video'
        }
      });
    }
  };

  handleDownloadVideo = async (video) => {
    try {
      this.setState({ 
        isDownloading: true, 
        downloadingVideoId: video.id 
      });
      
      this.showSnackbar('Đang bắt đầu tải xuống...', 'info');
      
      // Sử dụng videoId hoặc filePath
      const videoId = video.id;
      const fileName = video.filename;
      
      // Sử dụng stream-range endpoint để lấy file
      const response = await axios({
        url: `https://sperms.ailab.vn/api/stream?file_path=${encodeURIComponent(video.path)}`,
        method: 'GET',
        responseType: 'blob', // Quan trọng để nhận dữ liệu dạng blob
      });
      
      // Tạo URL cho blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Tạo phần tử a tạm thời để download
      const link = document.createElement('a');
      link.href = url;
      
      // Đặt tên file cho download
      link.setAttribute('download', fileName);
      
      // Thêm link vào DOM và click
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      this.showSnackbar('Tải xuống hoàn tất', 'success');
    } catch (error) {
      console.error('Error downloading video:', error);
      this.showSnackbar('Lỗi khi tải xuống video: ' + (error.message || 'Không xác định'), 'error');
    } finally {
      this.setState({ 
        isDownloading: false,
        downloadingVideoId: null
      });
    }
  };

  closeVideoDialog = () => {
    this.setState({
      openVideoDialog: false,
      selectedVideo: null
    });
  };

  formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm:ss');
    } catch (error) {
      return dateString;
    }
  };

  formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  getLastRefreshedTime = () => {
    if (!this.state.lastRefreshed) return 'Chưa làm mới';
    
    return format(this.state.lastRefreshed, 'HH:mm:ss');
  };

  render() {
    const { 
      videos, 
      selectedVideo, 
      openVideoDialog, 
      isLoading, 
      error, 
      snackbar,
      isDownloading,
      downloadingVideoId,
      page,
      limit,
      totalPages,
      totalItems,
      showFilters,
      filterByPatient,
      deleteDialog
    } = this.state;
    const { PatientID, patientName } = this.props;

    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <Grid container spacing={2}>
            <Grid item xs={12} className="flex justify-between items-center">
              <div>
                <Typography variant="h6" component="h2">
                  Danh sách video đã ghi
                  {isLoading && (
                    <CircularProgress 
                      size={20} 
                      color="primary" 
                      style={{ marginLeft: '10px', verticalAlign: 'middle' }} 
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {filterByPatient && PatientID && patientName ? `Bệnh nhân: ${patientName} (${PatientID})` : 'Tất cả video'}
                  {this.state.lastRefreshed && (
                    <span className="ml-2">• Cập nhật lúc: {this.getLastRefreshedTime()}</span>
                  )}
                  <span className="ml-2">• Tổng số: {totalItems} video</span>
                </Typography>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.toggleFilters}
                  size="small"
                  startIcon={<FilterListIcon />}
                >
                  Bộ lọc
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.fetchRecordedVideos}
                  disabled={isLoading}
                  size="small"
                  startIcon={<RefreshIcon />}
                >
                  Làm mới
                </Button>
              </div>
            </Grid>
            
            {showFilters && (
              <Grid item xs={12}>
                <Box className="p-3 bg-gray-50 rounded-md flex flex-wrap gap-3 items-center">
                  <FormControl size="small" style={{ minWidth: '200px' }}>
                    <InputLabel id="filter-type-label">Hiển thị video</InputLabel>
                    <Select
                      labelId="filter-type-label"
                      value={filterByPatient ? "patient" : "all"}
                      label="Hiển thị video"
                      onChange={() => this.handleFilterChange()}
                    >
                      <MenuItem value="all">Tất cả video</MenuItem>
                      <MenuItem value="patient" disabled={!PatientID}>Theo bệnh nhân hiện tại</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" style={{ minWidth: '100px' }}>
                    <InputLabel id="limit-label">Số lượng</InputLabel>
                    <Select
                      labelId="limit-label"
                      value={limit}
                      label="Số lượng"
                      onChange={this.handleLimitChange}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            )}
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider className="my-2" />
            </Grid>
            
            <Grid item xs={12}>
              {videos.length > 0 ? (
                <List>
                  {videos.map((video, index) => (
                    <React.Fragment key={video.id || index}>
                      <ListItem>
                        <ListItemIcon>
                          <VideoFileIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <div className="flex items-center gap-2">
                              {video.filename} 
                              {!video.fileExists && (
                                <Tooltip title="File không còn tồn tại trên server">
                                  <Chip
                                    size="small"
                                    label="File missing"
                                    color="error"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </div>
                          }
                          secondary={
                            <div>
                              <div>Ngày ghi: {this.formatDate(video.recordDate)}</div>
                              <div>Kích thước: {this.formatFileSize(video.fileSize)}</div>
                              <div>
                                {video.patientName && `Bệnh nhân: ${video.patientName}`}
                                {video.patientId && ` (${video.patientId})`}
                                {video.sampleId && ` | Mã mẫu: ${video.sampleId}`}
                              </div>
                            </div>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Xem">
                            <span>
                              <IconButton 
                                edge="end" 
                                aria-label="play"
                                onClick={() => this.handlePlayVideo(video)}
                                disabled={!video.fileExists}
                                size="medium"
                              >
                                <PlayArrowIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Tải xuống">
                            <span>
                              <IconButton 
                                edge="end" 
                                aria-label="download"
                                onClick={() => this.handleDownloadVideo(video)}
                                disabled={!video.fileExists || (isDownloading && downloadingVideoId === video.id)}
                                size="medium"
                              >
                                {isDownloading && downloadingVideoId === video.id ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  <DownloadIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Xóa (yêu cầu mã xác nhận)">
                            <IconButton 
                              edge="end" 
                              aria-label="delete"
                              onClick={() => this.handleOpenDeleteDialog(video)}
                              size="medium"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < videos.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box className="py-10 text-center">
                  <Typography variant="body1" gutterBottom>
                    Không có video nào được tìm thấy
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Hãy bấm nút "Record" để ghi video mới
                  </Typography>
                </Box>
              )}
            </Grid>
            
            {/* Phân trang */}
            {totalPages > 1 && (
              <Grid item xs={12} className="flex justify-center mt-4">
                <Pagination 
                  count={totalPages}
                  page={page}
                  onChange={this.handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Grid>
            )}
          </Grid>
        </CardContent>

        {/* Video Playback Dialog */}
        <Dialog
          fullWidth={true}
          maxWidth="lg"
          open={openVideoDialog}
          onClose={this.closeVideoDialog}
          aria-labelledby="video-dialog-title"
        >
          {selectedVideo && (
            <>
              <DialogTitle id="video-dialog-title">
                <div className="flex justify-between items-center">
                  <span>{selectedVideo.filename}</span>
                  <div className="flex gap-2">
                    {selectedVideo.patientName && (
                      <Chip 
                        icon={<InfoIcon />} 
                        label={`Bệnh nhân: ${selectedVideo.patientName}`}
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {selectedVideo.sampleId && (
                      <Chip 
                        icon={<InfoIcon />} 
                        label={`Mã mẫu: ${selectedVideo.sampleId}`}
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </div>
                </div>
              </DialogTitle>
              <DialogContent>
                <div className="w-full overflow-auto">
                  <video
                    controls
                    autoPlay
                    className="w-full max-w-full h-auto"
                    src={`https://sperms.ailab.vn/api/stream-range?file_path=${encodeURIComponent(selectedVideo.path)}`}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </DialogContent>
              <DialogActions className="p-4">
                <Button 
                  onClick={() => this.handleDownloadVideo(selectedVideo)}
                  color="primary"
                  disabled={isDownloading && downloadingVideoId === selectedVideo.id}
                  startIcon={
                    isDownloading && downloadingVideoId === selectedVideo.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                >
                  {isDownloading && downloadingVideoId === selectedVideo.id ? 'Đang tải...' : 'Tải xuống'}
                </Button>
                <Button
                  onClick={this.closeVideoDialog}
                  color="secondary"
                >
                  Đóng
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog 
          open={deleteDialog.open}
          video={deleteDialog.video}
          passcode={deleteDialog.passcode}
          expiresAt={deleteDialog.expiresAt}
          onClose={this.handleCloseDeleteDialog}
          onConfirm={this.handleDeleteVideo}
          isLoading={deleteDialog.isLoading}
          error={deleteDialog.error}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={this.closeSnackbar} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    );
  }
}

export default RecordedVideos;